import {
  CheckoutButtonColor,
  CheckoutButtonLayout,
  CheckoutButtonShape,
  CheckoutButtonTagline,
  CheckoutPaypalButtonText
} from '@components/buttons/types';
import type { PayPalButtonsComponentProps } from '@paypal/react-paypal-js';
import type { WorldReadyProps } from '@client/utils/localeHelper';
import { ShippingOptions } from '@components/ConfirmationCart/types';

/*
 U is union of all single key-value pairs in type T
 Therefore U[key of U] is any single key value pair
*/
type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> & U[keyof U];

interface LatmConf {
  trackSprigEvent: (
    event_name?: string,
    properties?: Record<string, string | number | boolean | Object>
  ) => void;
}

declare global {
  interface Window {
    __initialState__: any;
    ApplePaySession: typeof ApplePaySession | undefined;
    ApplePayError: typeof ApplePayError | undefined;
    PAYPAL?: {
      analytics?: {
        instance?: {
          recordClick: Function;
          recordImpression: Function;
          recordError: Function;
          logActivity: Function;
        };
        startCPLTracking: Function;
        endCPLTracking: Function;
      };
    };
    latmconf: LatmConf;
  }
}
export interface ServerData {
  requestURI: string;
  worldReady: WorldReadyProps;
  locality: {
    locale: string;
    language: string;
    timezone: {
      value: string;
      determiner: string;
    };
    directionality: string;
  };
  fpti: object;
  csrfToken: string;
  nonce: string;
  sdkUrl: string;
  sdkClientId: string;
  geoAwareDomain?: string;
  multiAccountData?: {
    isMultiAcct?: boolean;
    accounts?: {
      current?: boolean;
      accountIdentifier?: string;
      accountNumber?: string;
      alias?: string;
      businessName?: string;
    }[];
    user?: {
      accountNumber?: string;
      email?: string;
      isPrimaryUser?: boolean;
      accountType: string;
    };
    enterpriseData?: {
      isEnterpriseAdmin?: boolean;
      isInTenantHierarchy?: boolean;
      hasAdminView?: boolean;
      operationalAccount?: object;
    };
  };
  timezone?: string;
  accountCurrencies?: string[];
}

export interface UseRequestResult<T> {
  data: null | T;
  initialDataHasLoaded?: boolean;
  errors: unknown;
  pending: boolean;
}

export interface UseRequestOptions {
  url: string;
  query?: any;
  method?: string;
  methodPath?: string; // needed for additional info to be sent with PATCH/PUT
  methodOp?: string; // needed for additional info to be sent with PATCH/PUT
  listBased?: boolean;
}

export interface UseRequestCallbackOptions {
  query?: object;
  requestBody?: any;
  isClear?: boolean;
}

export type UseRequestCallbackFunction = (requestOptions?: UseRequestCallbackOptions) => void;

export interface MakeRequestOptions {
  method?: string;
  methodPath?: string; // needed for additional info to be sent with PATCH/PUT
  methodOp?: string; // needed for additional info to be sent with PATCH/PUT
  requestBody?: any;
  csrf?: string;
}

export type CurrentInventoryInfoArr = Array<CurrentInventoryInfo>;

export type OptionDetailArrType = Array<OptionDetail>;

export type HostedButtonDetails = {
  id: string;
  link_variables?: Array<{ name: string; value: string }>;
  country_code?: string;
  language?: string;
  status?: string;
  business_name?: string;
  option_details?: OptionDetailArrType;
  create_time: string;
  update_time?: string;
  eligibility?: boolean;
  inventory?: Array<CurrentInventoryInfo>;
};

export type CurrentInventoryInfo = {
  current_inventory: string;
  allow_over_sell: boolean;
  item_number?: string;
};

export type OptionDetail = {
  currency_code?: string;
  name: string;
  selection_details: Array<SelectionDetail>;
};

export type OptionItem = {
  primaryText: string;
  value: string;
  price?: string;
};

export type QuantityOption = {
  primaryText: string;
  value: string;
};

export type SelectionDetail = {
  price?: string;
  option_selection: string;
  images?: string;
  item_number?: string;
};

export type AmountDetails = {
  currency_code: string;
  value: string;
  breakdown: {
    item_total: Breakdown;
    shipping: Breakdown;
    handling: Breakdown;
    tax_total: Breakdown;
    insurance: Breakdown;
    shipping_discount: Breakdown;
    discount: Breakdown;
  };
};

export type OrderDetails = {
  buttonType?: 'FIXED_PRICE' | 'SINGLE_SELECT_LIST' | 'VARIABLE_PRICE';
  name?: string;
  error?: boolean;
  link_id?: string;
  merchant_id?: string;
  context_id?: string;
  transaction_id?: string;
  status?: string;
  merchant_name?: string;
  payment_status?: string;
  payer?: {
    name?: {
      given_name: string;
      surname: string;
    };
    email_address: string;
    payer_id: string;
  };
  shipping?: {
    name?: {
      full_name?: string;
    };
    address?: Address;
    options?: ShippingOptions[];
  };
  amount?: AmountDetails;
  funding_method?: string;
  items?: Array<{
    description: string;
    name: string;
    quantity: string;
    tax: Breakdown;
    unit_amount: Breakdown;
    memos?: Array<{ label: string; value: string }>; // Spec change
    shipping_preference?: string; // Spec change
    option_selections?: Array<{
      name: string;
      select: string;
    }>; // Spec change
    image_url?: string;
  }>;
  returnURL: string;
  shippingPreference?: string;
  checkoutButtonTextType?: string;
  orderDate?: string;
  description?: string;
  memoLabel?: string;
  invoiceLabel?: string;
  invoice_id?: string;
};

export type Breakdown = {
  currency_code: string;
  value: string;
};

export type ServerError = {
  error: boolean;
  name: string;
  message: string;
  status: number;
  details?: Array<{
    description?: string;
    field?: string;
    issue?: string;
    location?: string;
    value?: string;
  }>;
  isClientError?: boolean;
};

export type CartItemInputType = {
  image_url?: string;
  quantity?: string;
  memo?: string;
  option_selections?: OptionSelection[];
  item?: string; // TODO - This is added temporarily only for Preview. Please remove this prop, when DTNCPS-2122 story is completed
};

export type CartItemErrorType = {
  id?: string;
  link_id?: string;
  quantity?: string;
};

export type FormErrorMessageType = {
  isFormValid: boolean;
  isFocused: boolean;
  amount?: string;
  memo?: string;
  invoice?: string;
  item?: string;
  quantity?: string;
  cartItems?: Record<string, CartItemErrorType>;
  msgVariables: {
    min?: number;
    max?: number;
    currencySymbol?: string;
  };
  option_selections?: OptionSelection[];
};

export type UserInputsType = {
  quantity?: string;
  item?: string;
  amount?: string;
  memo?: string;
  invoice?: string;
  currency?: string;
  currencySymbol?: string;
  // TODO: add undefined as a possible value
  cartItems?: Record<string, CartItemInputType>;
  option_selections?: OptionSelection[];
  image_url?: string;
};

export enum PaymentSource {
  PAYPAL = 'paypal',
  CARD = 'card',
  VENMO = 'venmo',
  PAYLATER = 'paylater',
  GOOGLEPAY = 'googlepay',
  APPLEPAY = 'applepay'
}

export type Address = {
  address_line_1: string;
  admin_area_2: string;
  admin_area_1: string;
  postal_code: string;
  country_code: string;
};

export type CreatedOrder = {
  context_id: string;
  link_id: string;
  merchant_id: string;
  status: string;
};

export type CapturedOrder = {
  context_id: string;
  link_id: string;
  merchant_id: string;
  payment_status: string;
  status: string;
};

export type HostedButton = {
  id: string;
  type?: string;
  button_variables?: Array<{ name: string; value: string }>;
  country_code?: string;
  language?: string;
  status?: string;
  option_details?: Array<OptionDetail>;
  time_created: string;
  time_updated?: string;
};

export type ButtonStyleConfig = {
  layout: `${CheckoutButtonLayout}`;
  shape: `${CheckoutButtonShape}`;
  color: `${CheckoutButtonColor}`;
  button_text: `${CheckoutPaypalButtonText}`;
  tagline: `${CheckoutButtonTagline}` | boolean;
};

export type SDKButtonStyle = PayPalButtonsComponentProps['style'];

export type PaymentMethodConfig = {
  enabled: boolean;
  supportedCountries?: string[];
  supportedCurrencies?: string[];
};

export type UcpConfig = {
  enablePhase2SlowRamp: boolean;
  enablePhase2BuyerExperience: boolean;
  disablePhase2BuyerCountries: string[];
  disablePhase2ButtonCountries: string[];
  applepay: PaymentMethodConfig;
  venmo: PaymentMethodConfig;
  paylater: PaymentMethodConfig;
  card: PaymentMethodConfig;
  sprigFeedbackEventName: string;
  featureFlags: {
    doNotCollectShipping?: boolean;
    shippingTaxExpansion?: boolean;
    singleButton?: boolean;
    jsSDKThreeButtonExp?: boolean;
    venmoShipTaxSupport?: boolean;
    supportShippingTaxProfileInPayment?: boolean;
    paywallButtons?: boolean;
    productImage?: boolean;
    crossSell?: boolean;
    openGraphDescription?: boolean;
    openGraphMerchantUploadedImage?: boolean;
    transactionAttribution?: boolean;
    enableRiskLimitationCheck?: boolean;
  };
  paypalButtonMessaging: PaymentMethodConfig;
  commercePlatform: {
    systemPingLinks: string[];
    dualReadLinks: string[];
    dualWriteLinks: string[];
  };
};

export type OrderShipping = {
  context_id: string;
  amount: {
    breakdown: {
      item_total: Breakdown;
      shipping: Breakdown | undefined;
      tax_total: Breakdown | undefined;
    };
    currency_code: Breakdown['currency_code'];
    value: Breakdown['value'];
  };
  shipping_options:
    | Array<{
        amount: Breakdown;
        id: string;
        label: string;
        selected: boolean;
        type: string;
      }>
    | undefined;
};

export type PaymentAmountDetails = {
  itemPrice: string;
  quantity: number;
  shipping: string;
  taxRate: string;
};

export type PaymentBreakdownAmount = {
  subtotal: number;
  shipping: number;
  taxAmount: number;
  total: number;
};

export enum DeployEnvironment {
  PRODUCTION = 'production',
  SANDBOX = 'sandbox',
  STAGING = 'staging',
  DEVELOPMENT = 'development'
}

export type MerchantInfo = {
  businessName: string;
  merchantId: string;
  primaryCurrencyCode: string | null;
  businessLogo?: string;
  countryCode?: string;
};

export type OptionSelection = {
  name: string;
  select: string;
};

export type Memo = {
  label: string;
  value: string;
};

export type PaymentPrefillData = {
  hasVariants?: string;
  memo?: string;
  amount?: string;
  invoice?: string;
  quantity?: string;
  option_selections?: OptionSelection[];
  image_url?: string;
  selectedImageUrl?: string;
};

export enum ButtonVariation {
  BuyNow = 'BUY_NOW',
  Cart = 'ADD_TO_CART'
}

export enum FieldValueContent {
  EstimateLater = 'ESTIMATE_LATER',
  Free = 'FREE'
}

export type Field<T> = {
  showField: boolean;
  value: T;
};

export type TaxTotalField = Field<number | `${FieldValueContent.EstimateLater}`>;
export type ShippingField = Field<TaxTotalField['value'] | `${FieldValueContent.Free}`>;
export type SubtotalField = Field<number>;

export interface TotalFields {
  tax: TaxTotalField;
  shipping: ShippingField;
  subtotal: SubtotalField;
  total: TaxTotalField;
}

export type MerchantSettingsQuery = {
  categories?: string;
  groups?: string;
  preferences?: string;
};

export type MerchantPreferences = {
  categories: Category[];
};

export type Category = {
  name: string;
  description?: string;
  groups: Group[];
};

export type Group = {
  name: string;
  description?: string;
  preferences: Preference[];
};

export type Preference = {
  name: string;
  value: string;
  description?: string;
  status?: string;
};

export type PayLaterStatus = {
  isPayLaterDisabled: boolean;
};

export type MerchantSettings = {
  //checkout related settings
  payment_method_paylater?: boolean;
  payment_method_venmo?: boolean;
  payment_method_apple_pay?: boolean;
  // TODO: Support Google Pay once Google Pay integration provided
  // payment_method_google_pay?: boolean;
  // TODO: Support paypal button message once JSSDK inner source work completed
  // paypal_button_message?: boolean;
  show_business_name?: boolean;
  show_business_logo?: boolean;
  homepage_url?: string;
  //stacked button related settings
  stacked_button_layout?: string;
  stacked_button_shape?: string;
  stacked_button_size?: string;
  stacked_button_color_type?: string;
  stacked_button_second_button_type?: string;
  stacked_button_text_type?: string;
  stacked_button_custom_text?: string;
  //single button related settings
  single_button_shape?: string;
  single_button_size?: string;
  single_button_color_type?: string;
  single_button_color_code?: string;
  //cart button related settings
  cart_button_shape?: string;
  cart_button_size?: string;
  cart_button_color_type?: string;
  cart_button_color_code?: string;
  auto_return_url?: string;
};

export interface EnableMessagingObj {
  enabled: boolean;
  xe: string;
  xt: string;
}

export type DynamicMediaImage = {
  assetId: string;
  links: {
    href: string;
    rel?: string;
    sizeId?: number;
    width?: number;
    height?: number;
  }[];
};

export enum ImageStatus {
  ACTIVE = 'active',
  IDLE = 'idle',
  LOADING = 'loading'
}

export type DynamicImageUrl = string;

export type DynamicImageActive = {
  status: ImageStatus.ACTIVE;
  assetId: string;
  exampleImageUrl: DynamicImageUrl; // e.g. https://pics.msmaster.qa.paypal.com/00/p/OTJiNzU3MDAtNDQ3NS00M2QxLWJkYTItZDYyN2FiYzQ1OTVk/image_12.PNG, _12 can be replaced by other size ids.
};

export type DynamicImageWithStatus =
  | {
      status: ImageStatus.IDLE | ImageStatus.LOADING;
      assetId?: never;
    }
  | DynamicImageActive;

/**
 * Refer to https://paypal.atlassian.net/wiki/spaces/PayPalMediaService/pages/471040357/Image+Request+Metadata+Data#ImageRequestMetadataData-ImageSizeIds for more size ids
 */
export enum DynamicImageSizeIds {
  DIMENSION_80x80 = 23,
  DIMENSION_200x200 = 2,
  DIMENSION_500x500 = 12,
  DIMENSION_800x800 = 3
}

export type ImageItem = {
  id: string;
  type?: 'product' | 'variant';
  value?: string; // Each variant image should have a value to match the corresponding option.
  thumbnailUrl?: string;
  heroUrl?: string;
  altText?: string;
  showSoldOut?: boolean;
};

export enum ImageError {
  TYPE_ERROR = 'typeError',
  SIZE_ERROR = 'sizeError',
  SERVER_ERROR = 'serverError',
  DIMENSION_TOO_LOW_ERROR = 'dimensionLowError',
  DIMENSION_TOO_HIGH_ERROR = 'dimensionHighError'
}

export type FptiAdditionalTags = Record<string, string | object | boolean | undefined>;

export type InterstitialExperimentDetails =
  | {
      isEnabled: true;
      isTreatment: boolean;
      experimentId: string;
      treatmentId: string;
    }
  | {
      isEnabled: false;
    };

export type InventoryBFSPayload = {
  id: string;
  track_inventory: boolean;
  track_pnl: boolean; // This field was used in WPS. There is no product requirement and UI related to this field by now. Default to false.
  inventory_overridden?: boolean; // allow sold out
  soldout_url?: string;
  currency_code?: {
    currency_code: string;
  };
  item_tracking_details?: {
    item_number: string;
    item_quantity: number; // Pay attention quantity in product inventory is number, but is string in variant inventory.
    item_alert: number; // Pay attention alert quantity in product inventory is number, but is string in variant inventory.
  };
  option_tracking_details?: {
    item_number: string; // item id
    option_quantity: string; // quantity in stock for this option
    option_alert: string; // alert quantity for this option
    option_number: string; // variant’s index, always 0 if only supports one variant's inventory
    option_name: string; // variant’s name
    option_selection_number: string; // this option’s index
    option_selection_name: string; // this option’s name
  }[]; // variant level inventory
  option_index?: string; // variant’s index, always 0 if only supports one variant's inventory
};

export type InventoryFromBFS = {
  data: InventoryBFSPayload[]; // Inventory fetched from BFS is an array with similar InventoryBFSPayload structure. Multiple variant options will be flattened to multiple elements in this array.
  error: boolean;
};

export enum ButtonErrorField {
  INVENTORY = 'inventory'
}

export type ButtonAPIResponse = {
  button: HostedButton;
  errors: (ServerError & {
    field: ButtonErrorField;
  })[];
};
